import { useEffect, useRef } from 'react';
import { c, card } from '../../theme';
import { fmtPct } from './format';

// The Executive Overview's daily service-level panel — the old page's
// `sl_trend`, opened from its Service level card.
//
// One row per day, a bar the length of the day's service level, green where
// the day met target and red where it did not, with the count of days that
// met it. It answers a question the gauge cannot: a month at 94% is a steady
// 94 or three bad Mondays, and those need different conversations.
//
// ── Sundays ─────────────────────────────────────────────────────────────────
// Dropped, as the old panel drops them (`getDay() !== 0`). Athens' support
// lines are closed on Sundays, so a Sunday either has no calls and no service
// level, or a handful of stragglers whose 50% would sit in the list looking
// like a collapse. The count of days meeting target is over the days shown,
// so the two figures on the panel agree with each other.
//
// ── Which ranges ────────────────────────────────────────────────────────────
// Month to date, Last month and Custom. Not Today — a one-day trend is the
// gauge with extra steps — which is the old page's own rule for when it
// offers the button.

const isSunday = (ymd) => new Date(`${ymd}T12:00:00Z`).getUTCDay() === 0;
const dayLabel = (ymd) => new Date(`${ymd}T12:00:00Z`)
  .toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });
const weekday = (ymd) => new Date(`${ymd}T12:00:00Z`)
  .toLocaleDateString('en-US', { timeZone: 'UTC', weekday: 'short' });

/**
 * The rows the panel draws: the window's days, Sundays removed.
 *
 * Exported for the test, because the two decisions in it are the ones that
 * would be wrong silently — a Sunday left in reads as a failing day, and a
 * day with no calls counted as missing target would make a closed holiday
 * look like a service failure.
 */
export function slTrendRows(daily, target, today) {
  const rows = (daily || [])
    .filter((d) => !isSunday(d.date))
    .map((d) => ({
      date: d.date,
      label: dayLabel(d.date) + (d.date === today ? ' · today' : ''),
      sub: weekday(d.date),
      value: d.serviceLevel,
      hit: d.serviceLevel != null && d.serviceLevel >= target,
    }));
  const measured = rows.filter((r) => r.value != null);
  return { rows, met: measured.filter((r) => r.hit).length, measured: measured.length };
}

export default function ExecSlTrendPanel({ daily, target, today, rangeLabel, onClose }) {
  const closeRef = useRef(null);

  // Escape closes it, and focus starts on the close button — a panel that
  // traps a keyboard reader on the page behind it is worse than no panel.
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { rows, met, measured } = slTrendRows(daily, target, today);

  return (
    <section style={s.panel} role="region" aria-label="Daily service level">
      <div style={s.head}>
        <div>
          <h3 style={s.title}>Service level by day</h3>
          <p style={s.sub}>
            {rangeLabel} · target {target}%
            {measured > 0 && ` · ${met} of ${measured} day${measured === 1 ? '' : 's'} met it`}
            {' · Sundays not shown'}
          </p>
        </div>
        <button ref={closeRef} type="button" onClick={onClose} style={s.close} aria-label="Close the daily service level">
          Close
        </button>
      </div>

      {!rows.length && <p style={s.empty}>No days in this window.</p>}

      <ol style={s.list}>
        {rows.map((r) => (
          <li key={r.date} style={s.row}>
            <span style={s.day}>
              {r.label}
              <span style={s.weekday}>{r.sub}</span>
            </span>
            <span style={s.track}>
              {/* A day with no calls draws no bar at all rather than a bar of
                  zero: nothing happened, which is not the same as failing. */}
              {r.value != null && (
                <span style={{
                  ...s.bar,
                  width: `${Math.max(1, Math.min(100, r.value))}%`,
                  background: r.hit ? c.success : c.danger,
                }} />
              )}
            </span>
            <span style={{ ...s.value, color: r.value == null ? c.textFaint : r.hit ? c.successText : c.dangerText }}>
              {r.value == null ? 'no calls' : fmtPct(r.value)}
            </span>
          </li>
        ))}
      </ol>
    </section>
  );
}

const s = {
  panel:   { ...card.base, padding: '14px 18px 16px', marginTop: '4px' },
  head:    { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' },
  title:   { fontSize: '14px', fontWeight: 700, color: c.text, margin: 0 },
  sub:     { fontSize: '11.5px', color: c.textMuted, margin: '2px 0 0' },
  close:   { all: 'unset', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: c.textMuted,
    border: `1px solid ${c.border}`, borderRadius: '6px', padding: '5px 10px' },
  list:    { listStyle: 'none', margin: '12px 0 0', padding: 0, display: 'flex', flexDirection: 'column', gap: '4px' },
  row:     { display: 'grid', gridTemplateColumns: 'minmax(96px, 128px) 1fr 68px', alignItems: 'center', gap: '10px' },
  day:     { fontSize: '11.5px', color: c.textStrong, display: 'flex', alignItems: 'baseline', gap: '5px' },
  weekday: { fontSize: '10.5px', color: c.textFaint },
  track:   { height: '10px', borderRadius: '5px', background: c.surfaceSunk, overflow: 'hidden' },
  bar:     { display: 'block', height: '100%', borderRadius: '5px' },
  value:   { fontSize: '11.5px', fontWeight: 600, textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
  empty:   { fontSize: '12px', color: c.textMuted, margin: '10px 0 0' },
};
